import { useState, type ReactNode } from 'react';
import type { Map as MlMap } from 'maplibre-gl';
import { Button, DriverCard, Handle, MapSheet, SheetFoot, StatusHeader } from '../components/ui';
import { Icon } from '../components/Icon';
import { MapLine, useFit, useGlide } from '../components/MapView';
import { useApp } from '../lib/app';
import { clock, rupees } from '../lib/format';
import { roadEstimate } from '../lib/geo';
import { describePoint } from '../lib/places';
import { cancelTerms } from '../lib/rideScreen';
import type { Ride } from '../lib/types';
import { colourNoun, words } from '../lib/vehicles';
import { useT } from '../lib/i18n';
import { useColours } from '../theme/usePalette';
import { MapPill, MapStrip, VehicleDisc, YouDot } from './mapBits';
import { SafetySheet, SosSheet } from './Safety';

export function Arriving({ ride, onCancel, cancelling, onShare, shareNote, map }: { ride: Ride; onCancel: () => void; cancelling: boolean; onShare: () => void; shareNote: ReactNode; map: MlMap | null }) {
  const { settings } = useApp();
  const t = useT();
  const pal = useColours('map');
  const [safety, setSafety] = useState(false);
  const [sos, setSos] = useState(false);
  const d = ride.driver;
  const name = d?.firstName ?? t('common.yourDriver');
  const loc = d?.location ?? null;
  const here = ride.status === 'ARRIVED';
  const est = loc ? roadEstimate(loc, ride.pickup) : null;
  const mins = est ? Math.max(1, Math.round(est.seconds / 60)) : null;
  const eta = est ? clock(new Date(Date.now() + est.seconds * 1000)) : null;
  const vehicleWords = d?.vehicle ? colourNoun(t, d.vehicle.colour, ride.vehicleType) : words(t, ride.vehicleType).noun;
  const terms = cancelTerms(ride, settings);
  const spot = describePoint(ride.pickup);
  const youLabel = spot.title.includes('near ') ? t('arr.youAt', { place: spot.title.split('near ')[1] }) : t('arr.you');
  const pin = (ride.pin ?? '----').split('');
  // the line from the driver follows the gliding marker
  const shown = useGlide(loc ?? ride.pickup, 1600);

  useFit(map, { points: loc ? [loc, ride.pickup] : [ride.pickup], padding: { top: 100, bottom: 170, left: 110, right: 110 }, maxZoom: 17 });

  return (
    <>
      {loc && !here && <MapLine id="approach" coords={[[shown.lng, shown.lat], [ride.pickup.lng, ride.pickup.lat]]} color={pal.route} width={3.4} casing={pal.routeCase} casingWidth={7} />}
      <YouDot at={ride.pickup} />
      <MapPill at={ride.pickup} side="left" gap={18}>
        <span style={{ fontSize: 12.5 }}>{youLabel}</span>
      </MapPill>
      {loc && <VehicleDisc at={loc} type={ride.vehicleType} heading={loc.heading} />}

      <button
        type="button"
        aria-label={t('arr.safetyAria')}
        onClick={() => setSafety(true)}
        className="zr-pill"
        style={{ position: 'absolute', right: 20, top: 'calc(var(--sat) + 10px)', height: 44, padding: '0 14px 0 12px', border: 0, borderRadius: 22, gap: 8, cursor: 'pointer', zIndex: 5 }}
      >
        <Icon name="shield2" size={18} />
        {t('arr.safety')}
      </button>

      <MapSheet label={t('arr.sheet')} art={<MapStrip />}>
        <div className="zr-swap" style={{ display: 'flex', flexDirection: 'column' }}>
          <Handle />
          <StatusHeader
            style={{ marginTop: 18 }}
            title={here ? t('arr.here', { name }) : t('arr.coming', { name })}
            sub={here ? t('arr.waiting', { vehicle: vehicleWords }) : eta ? t('arr.arriving', { time: eta, vehicle: vehicleWords }) : t('arr.onWay', { vehicle: vehicleWords })}
            big={here ? t('common.here') : (mins ?? '–')}
            unit={here ? undefined : t('common.min')}
            bigTone={here ? 'ac' : undefined}
          />

          <div className="zr-card" style={{ marginTop: 16, padding: '14px 14px 14px 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 15, lineHeight: '20px', fontWeight: 700 }}>{t('arr.pin')}</div>
              <div style={{ marginTop: 2, fontSize: 13, lineHeight: '18px', fontWeight: 500, color: 'var(--ink2)' }}>{t('arr.sayPin', { name })}</div>
            </div>
            <div role="img" aria-label={t('arr.pinAria', { pin: pin.join(' ') })} data-testid="ride-pin" data-pin={ride.pin ?? ''} style={{ flex: 'none', display: 'flex', gap: 6 }}>
              {pin.map((c, i) => (
                <span key={i} style={{ width: 36, height: 48, borderRadius: 12, background: 'var(--pinBg)', color: 'var(--pinInk)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 24, fontWeight: 900, fontVariantNumeric: 'tabular-nums', animation: `zr-popin .28s var(--ease-out) ${i * 50}ms both` }}>
                  {c}
                </span>
              ))}
            </div>
          </div>

          <DriverCard
            style={{ marginTop: 10 }}
            name={name}
            plate={d?.vehicle?.plate}
            line={
              <>
                <Icon name="star" size={13} style={{ color: 'var(--acText)' }} />
                {d?.rating != null ? d.rating.toFixed(1) : t('common.new')}
                {d?.completedRides != null && ` · ${t.n('common.rides', d.completedRides)}`}
              </>
            }
          />

          {/* calling and messaging the driver need a masked-calling provider: not shown until there is one */}
          <div style={{ marginTop: 10 }}>
            <Button variant="secondary" icon="share" onClick={onShare}>
              {t('safety.share')}
            </Button>
          </div>
          {shareNote}

          {ride.pickupNote && (
            <p data-testid="pickup-note" className="zr-ellipsis" style={{ margin: '10px 2px 0', display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, lineHeight: '18px', fontWeight: 500, color: 'var(--ink2)' }}>
              <Icon name="note" size={16} sw={1.9} style={{ flex: 'none' }} />
              {t('arr.note', { note: ride.pickupNote })}
            </p>
          )}
        </div>

        <SheetFoot style={{ marginTop: 'auto', paddingTop: 'calc(12px * var(--vg))' }}>
          <button type="button" disabled={cancelling} onClick={onCancel} aria-haspopup="dialog" className="zr-btn zr-btn--ghost" style={{ width: '100%' }}>
            {cancelling ? t('common.cancelling') : t('arr.cancel')}{' '}
            <span style={{ fontWeight: 500, color: 'var(--ink3)' }}>
              · {terms.free ? (terms.until ? t('arr.freeUntil', { time: clock(terms.until) }) : t('common.free')) : t('arr.fee', { fee: rupees(terms.feePaise) })}
            </span>
          </button>
        </SheetFoot>
      </MapSheet>

      <SafetySheet open={safety} onClose={() => setSafety(false)} onSos={() => (setSafety(false), setSos(true))} onShare={() => (setSafety(false), onShare())} />
      <SosSheet open={sos} onClose={() => setSos(false)} ride={ride} />
    </>
  );
}
